import * as THREE from 'three';

/**
 * Renders the world scene into a fixed-height internal render target, then
 * nearest-upscales it to the canvas. The low-res RT is both the 1994 look
 * (chunky pixels) and the main mobile perf lever (fill rate).
 */
const RENDER_HEIGHT = 540; // internal RT height; drop to ~400 on slow devices

export class GameRenderer {
  readonly renderer: THREE.WebGLRenderer;
  private rt: THREE.WebGLRenderTarget;
  private postScene: THREE.Scene;
  private postCamera: THREE.OrthographicCamera;
  private postMaterial: THREE.ShaderMaterial;
  private renderHeight = RENDER_HEIGHT;

  constructor(canvas: HTMLCanvasElement) {
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: false,
      powerPreference: 'high-performance',
      stencil: false,
    });
    this.renderer.setPixelRatio(1); // upscale is done by the post pass
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.NoToneMapping;

    this.rt = this.createRenderTarget(16, 16);

    this.postMaterial = new THREE.ShaderMaterial({
      uniforms: {
        tDiffuse: { value: this.rt.texture },
        uCrt: { value: 0 },
        uRtHeight: { value: RENDER_HEIGHT },
      },
      depthTest: false,
      depthWrite: false,
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = vec4(position.xy, 0.0, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        uniform sampler2D tDiffuse;
        uniform float uCrt;
        uniform float uRtHeight;
        varying vec2 vUv;

        float bayer4(vec2 p) {
          // 4x4 ordered-dither threshold, 0..1
          vec2 q = floor(mod(p, 4.0));
          float i = q.y * 4.0 + q.x;
          float b =
            i == 0.0 ? 0.0 : i == 1.0 ? 8.0 : i == 2.0 ? 2.0 : i == 3.0 ? 10.0 :
            i == 4.0 ? 12.0 : i == 5.0 ? 4.0 : i == 6.0 ? 14.0 : i == 7.0 ? 6.0 :
            i == 8.0 ? 3.0 : i == 9.0 ? 11.0 : i == 10.0 ? 1.0 : i == 11.0 ? 9.0 :
            i == 12.0 ? 15.0 : i == 13.0 ? 7.0 : i == 14.0 ? 13.0 : 5.0;
          return b / 16.0;
        }

        void main() {
          vec2 uv = vUv;
          if (uCrt > 0.5) {
            // slight barrel distortion
            vec2 c = uv - 0.5;
            uv = 0.5 + c * (1.0 + 0.05 * dot(c, c) * 4.0);
            if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) {
              gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0);
              return;
            }
          }
          vec4 col = texture2D(tDiffuse, uv);
          if (uCrt > 0.5) {
            // scanlines locked to internal RT rows
            col.rgb *= 0.90 + 0.10 * sin(uv.y * uRtHeight * 3.14159265);
            // palette crush + ordered dither (the 1994 look)
            float d = (bayer4(gl_FragCoord.xy) - 0.5) / 20.0;
            col.rgb = floor((col.rgb + d) * 30.0) / 30.0;
            // vignette
            vec2 q = vUv - 0.5;
            col.rgb *= 1.0 - 0.55 * dot(q, q);
          }
          gl_FragColor = col;
          #include <colorspace_fragment>
        }
      `,
    });

    this.postScene = new THREE.Scene();
    this.postCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.postMaterial);
    quad.frustumCulled = false;
    this.postScene.add(quad);

    this.resize();
    window.addEventListener('resize', () => this.resize());
    // layout can settle (or change) without a window resize event — a
    // zero-sized canvas at construction means aspect 0 and a sky-only
    // degenerate projection until something re-measures
    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(() => this.resize()).observe(canvas);
    }
  }

  private createRenderTarget(w: number, h: number): THREE.WebGLRenderTarget {
    const rt = new THREE.WebGLRenderTarget(w, h, {
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      generateMipmaps: false,
      depthBuffer: true,
    });
    // Scene colors are sRGB-encoded on write into the 8-bit RT (matches what
    // a direct-to-canvas render would store), decoded on sample in the post
    // pass, re-encoded by colorspace_fragment. Round-trips exactly.
    rt.texture.colorSpace = THREE.SRGBColorSpace;
    return rt;
  }

  get aspect(): number {
    const c = this.renderer.domElement;
    const w = c.clientWidth || window.innerWidth || 360;
    const h = c.clientHeight || window.innerHeight || 640;
    return Math.max(0.2, w) / Math.max(1, h);
  }

  /** Perf lever: lower internal resolution on slow devices. */
  setRenderHeight(h: number): void {
    this.renderHeight = h;
    this.resize();
  }

  /** The optional CRT/dither pass (scanlines, palette crush, vignette, barrel). */
  setCrt(enabled: boolean): void {
    const u = this.postMaterial.uniforms as { uCrt: { value: number } };
    u.uCrt.value = enabled ? 1 : 0;
  }

  resize(): void {
    const c = this.renderer.domElement;
    // hidden/pre-layout tabs can report zero sizes — never collapse the RT
    const w = Math.max(16, c.clientWidth || window.innerWidth || 360);
    const h = Math.max(16, c.clientHeight || window.innerHeight || 640);
    this.renderer.setSize(w, h, false);
    const rtH = Math.max(16, Math.min(this.renderHeight, h));
    const rtW = Math.max(16, Math.round((w / h) * rtH));
    this.rt.setSize(rtW, rtH);
    const u = this.postMaterial.uniforms as { uRtHeight: { value: number } };
    u.uRtHeight.value = rtH;
  }

  render(scene: THREE.Scene, camera: THREE.Camera): void {
    this.renderer.setRenderTarget(this.rt);
    this.renderer.render(scene, camera);
    this.renderer.setRenderTarget(null);
    this.renderer.render(this.postScene, this.postCamera);
  }

  /** Dev tooling: read back the internal RT as a data URL (pixel-exact frame). */
  captureDataUrl(quality = 0.7): string {
    const { width, height } = this.rt;
    const pixels = new Uint8Array(width * height * 4);
    this.renderer.readRenderTargetPixels(this.rt, 0, 0, width, height, pixels);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d')!;
    const image = ctx.createImageData(width, height);
    // GL rows are bottom-up
    for (let y = 0; y < height; y++) {
      const src = (height - 1 - y) * width * 4;
      image.data.set(pixels.subarray(src, src + width * 4), y * width * 4);
    }
    ctx.putImageData(image, 0, 0);
    return canvas.toDataURL('image/jpeg', quality);
  }
}
